import React, { useEffect, useRef } from 'react';

interface FrequencyThumbnailProps {
    spectralProfile: number[]; // 24 values representing frequency bands
    width?: number;
    height?: number;
    className?: string;
}

export const FrequencyThumbnail: React.FC<FrequencyThumbnailProps> = ({
    spectralProfile,
    width = 120,
    height = 40,
    className = ''
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !spectralProfile || spectralProfile.length === 0) {
            return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Set canvas size for crisp rendering
        const dpr = window.devicePixelRatio || 1;
        canvas.width = width * dpr;
        canvas.height = height * dpr;
        ctx.scale(dpr, dpr);

        // Clear canvas
        ctx.clearRect(0, 0, width, height);

        // Calculate energy distribution across frequency ranges
        const bassEnergy = spectralProfile.slice(0, 8).reduce((sum, val) => sum + val, 0);
        const midEnergy = spectralProfile.slice(8, 16).reduce((sum, val) => sum + val, 0);
        const trebleEnergy = spectralProfile.slice(16, 24).reduce((sum, val) => sum + val, 0);
        const totalEnergy = bassEnergy + midEnergy + trebleEnergy;

        if (totalEnergy === 0) {
            return;
        }

        // Normalize ratios
        const bassRatio = bassEnergy / totalEnergy;
        const midRatio = midEnergy / totalEnergy;
        const trebleRatio = trebleEnergy / totalEnergy;

        // Find the peak frequency band
        const maxIndex = spectralProfile.indexOf(Math.max(...spectralProfile));
        const peakPosition = maxIndex / spectralProfile.length; // 0-1

        // Create diverse hue mapping deterministically
        const hue = Math.floor(
            (bassRatio * 20) +      // Bass contributes reds (0-20)
            (midRatio * 140) +      // Mids contribute oranges-greens (0-140)
            (trebleRatio * 200) +   // Treble contributes blues-purples (0-200)
            (peakPosition * 120)    // Peak position adds variation (0-120)
        ) % 360;

        // Calculate saturation based on energy distribution uniformity
        const variance = Math.abs(bassRatio - 0.33) + Math.abs(midRatio - 0.33) + Math.abs(trebleRatio - 0.33);
        const saturation = Math.min(90, 45 + (variance * 150)); // 45-90%

        // Calculate lightness based on peak intensity
        const maxValue = Math.max(...spectralProfile);
        const avgValue = totalEnergy / spectralProfile.length;
        const dynamicRange = maxValue / (avgValue || 0.001);
        const lightness = Math.min(65, Math.max(30, 45 + (dynamicRange * 5) - (maxValue * 200))); // 30-65%

        const color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;

        // Draw solid color with subtle gradient
        const gradient = ctx.createLinearGradient(0, 0, width, height);
        gradient.addColorStop(0, color);
        gradient.addColorStop(1, `hsl(${hue}, ${saturation}%, ${Math.max(20, lightness - 15)}%)`);
        
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);

        // Draw music note icon - exact match to placeholder SVG
        const iconSize = Math.min(width, height) * 0.35;
        const centerX = width / 2;
        const centerY = height / 2;

        // Determine if background is light or dark for contrasting icon
        const iconColor = lightness > 50 ? 'rgba(0, 0, 0, 0.35)' : 'rgba(255, 255, 255, 0.35)';
        
        ctx.save();
        ctx.translate(centerX - (12 * iconSize / 24), centerY - (12 * iconSize / 24));
        ctx.scale(iconSize / 24, iconSize / 24);
        
        // Use exact same SVG path as placeholder
        const notePath = new Path2D('M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3');
        
        ctx.strokeStyle = iconColor;
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.stroke(notePath);
        
        ctx.restore();

    }, [spectralProfile, width, height]);

    // Show placeholder if no analysis data
    if (!spectralProfile || spectralProfile.length === 0) {
        return (
            <div 
                className={`bg-gray-100 rounded flex items-center justify-center ${className}`}
                style={{ width, height }}
            >
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                </svg>
            </div>
        );
    }

    return (
        <canvas
            ref={canvasRef}
            className={`rounded ${className}`}
            style={{ width, height }}
        />
    );
};
